import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cnpjValido } from "@/lib/validacao-cadastro";

export default function CadastroEmpresa() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    razao_social: "", nome_fantasia: "", cnpj: "",
    email: "", password: "", senha2: "",
    telefone_comercial: "", responsavel_legal: "",
    cidade: "", estado: "", nichos: [],
    endereco: "", numero: "", complemento: "", bairro: "", cep: "",
    telefone_financeiro: "", telefone_operacional: "", telefone_posto: "",
    email_administrativo: "", email_financeiro: "",
  });
  const [catalogo, setCatalogo] = useState({ nichos: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch("/api/catalogo/nichos").then(r => r.json()).then(setCatalogo).catch(() => {});
  }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleNicho = (n) => {
    setForm(f => ({
      ...f,
      nichos: f.nichos.includes(n) ? f.nichos.filter(x => x !== n) : [...f.nichos, n],
    }));
  };

  const submit = async () => {
    if (form.password !== form.senha2) { toast.error("As senhas não conferem"); return; }
    if (!form.razao_social || !form.cnpj || !form.email || !form.password) {
      toast.error("Preencha razão social, CNPJ, e-mail e senha"); return;
    }
    if (!cnpjValido(form.cnpj)) {
      toast.error("CNPJ inválido — confira os 14 dígitos e os dígitos verificadores"); return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.senha2;
      ["email_administrativo", "email_financeiro"].forEach(k => { if (!payload[k]) delete payload[k]; });
      await api.post("/auth/register-empresa", payload);
      toast.success("Cadastro realizado! Faça login para acessar o painel.");
      navigate("/login");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-5 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-7 h-7 brand-bg" />
            <span className="font-heading font-extrabold">MontaJá</span>
          </Link>
          <Link to="/login" className="text-sm hover:underline" data-testid="ce-login">Já tenho conta</Link>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="label-cap">Cadastro</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Seja uma empresa MontaJá</h1>
        <p className="text-xs text-muted-foreground mt-2 max-w-xl">
          Conecte sua operação a montadoras regionais com score, capacidade comprovada e auditoria completa.
        </p>

        <div className="mt-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <FF label="Razão social *" v={form.razao_social} on={(v) => upd("razao_social", v)} testid="ce-razao" />
            <FF label="Nome fantasia" v={form.nome_fantasia} on={(v) => upd("nome_fantasia", v)} testid="ce-fantasia" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <FF label="CNPJ *" v={form.cnpj} on={(v) => upd("cnpj", v)} testid="ce-cnpj" />
            <FF label="Telefone comercial" v={form.telefone_comercial} on={(v) => upd("telefone_comercial", v)} testid="ce-tel" />
          </div>
          <FF label="Responsável legal" v={form.responsavel_legal} on={(v) => upd("responsavel_legal", v)} testid="ce-resp" />
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2"><FF label="Endereço (rua)" v={form.endereco} on={(v) => upd("endereco", v)} testid="ce-endereco" /></div>
            <FF label="Número" v={form.numero} on={(v) => upd("numero", v)} testid="ce-numero" />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <FF label="Complemento" v={form.complemento} on={(v) => upd("complemento", v)} testid="ce-complemento" />
            <FF label="Bairro" v={form.bairro} on={(v) => upd("bairro", v)} testid="ce-bairro" />
            <FF label="CEP" v={form.cep} on={(v) => upd("cep", v)} testid="ce-cep" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <FF label="Cidade" v={form.cidade} on={(v) => upd("cidade", v)} testid="ce-cidade" />
            <FF label="Estado (UF)" v={form.estado} on={(v) => upd("estado", v)} testid="ce-uf" />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <FF label="Telefone financeiro" v={form.telefone_financeiro} on={(v) => upd("telefone_financeiro", v)} testid="ce-tel-fin" />
            <FF label="Telefone operacional" v={form.telefone_operacional} on={(v) => upd("telefone_operacional", v)} testid="ce-tel-op" />
            <FF label="Tel. do posto / resp. montagem" v={form.telefone_posto} on={(v) => upd("telefone_posto", v)} testid="ce-tel-posto" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <FF label="E-mail administrativo" type="email" v={form.email_administrativo} on={(v) => upd("email_administrativo", v)} testid="ce-email-adm" />
            <FF label="E-mail financeiro" type="email" v={form.email_financeiro} on={(v) => upd("email_financeiro", v)} testid="ce-email-fin" />
          </div>

          <div>
            <Label className="label-cap">Nichos atendidos</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {(catalogo.nichos || []).map(n => (
                <button key={n} type="button" onClick={() => toggleNicho(n)}
                  className={`btn-sharp p-3 text-sm capitalize border ${
                    form.nichos.includes(n) ? "brand-bg text-white border-transparent" : "border-border bg-white hover:bg-secondary"
                  }`}
                  data-testid={`ce-nicho-${n}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-5">
            <div className="label-cap mb-2">Acesso ao painel</div>
            <div className="grid md:grid-cols-3 gap-4">
              <FF label="E-mail *" type="email" v={form.email} on={(v) => upd("email", v)} testid="ce-email" ac="off" nm="ce_novo_email" />
              <FF label="Senha *" type="password" v={form.password} on={(v) => upd("password", v)} testid="ce-senha" ac="new-password" nm="ce_nova_senha" />
              <FF label="Confirmar senha *" type="password" v={form.senha2} on={(v) => upd("senha2", v)} testid="ce-senha2" ac="new-password" nm="ce_nova_senha2" />
            </div>
          </div>

          <p className="text-xs text-muted-foreground border-l-4 border-yellow-500 bg-yellow-50 p-3">
            Seu cadastro será analisado pelo Admin MontaJá antes de liberar a operação completa.
          </p>

          <Button onClick={submit} disabled={loading}
            className="w-full btn-sharp brand-bg text-white hover:bg-[#001A5E] h-12"
            data-testid="ce-submit">
            {loading ? "Cadastrando…" : "Criar conta e acessar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FF({ label, v, on, testid, type="text", ac, nm }) {
  const [ro, setRo] = useState(!!ac);
  return (
    <div>
      <Label className="label-cap">{label}</Label>
      <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)}
        autoComplete={ac} name={nm} readOnly={ro} onFocus={() => setRo(false)}
        className="mt-2" data-testid={testid} />
    </div>
  );
}
